import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import { T } from 'react-translator-component';
import { currencyFormat, i18nValue } from '../../functions';
import QuestionTotals from './QuestionTotals';
import QuestionSelectDay from './QuestionSelectDay';
import QuestionPaxQtySelector from './QuestionPaxQtySelector';
import { getQuantityFromValues, getSettingsBookButtonDisabled } from './QuestionTotals.utils';

const Settings = ({ settings, addon = null, currency = null, handleAddToCart, handleOnChange = null, showButtons = true, showCancel = true, onCancel, language, checkin = null }) => {
    const [values, setValues] = useState([]);
    const [disableBookButton, setDisableBookButton] = useState(true);
    const [resultColoquial, setResultColoquial] = useState("");

    const { type, data } = useMemo(() => {
        if (settings) {
            if (settings.questions) {
                return { type: "questions", data: settings.questions }
            }
        }
        return { type: null, data: null }
    }, [settings]);

    // ✅ Memoizar el callback para evitar loop infinito en QuestionTotals
    const handlePriceChange = useCallback((totalPrice) => {
        // Usar setState con función para siempre tener el estado actual
        setValues(prevValues => {
            let auxValues = [...prevValues];
            const keyExists = auxValues.findIndex(item => item['total'] !== undefined);
            
            if (totalPrice !== null && totalPrice !== undefined && totalPrice > 0) {
                // Agregar o actualizar el total válido
                if (keyExists >= 0) {
                    auxValues[keyExists] = { total: totalPrice };
                } else {
                    auxValues.push({ total: totalPrice });
                }
            } else {
                // Remover el total si es null o <= 0 (precios inválidos)
                auxValues = auxValues.filter(el1 => el1['total'] === undefined);
            }
            
            return auxValues;
        });
    }, [settings?.questions]);

    useEffect(() => {
        if (handleOnChange) {
            let auxValues = [...values];
            let hasKeyTotal = auxValues.find(item => item['total'] !== undefined);
            let hasKeyDates = auxValues.find(item => item['dates'] !== undefined);
            let hasQty = auxValues.find(item => item['qty'] !== undefined);
            const qtyFromHelper = getQuantityFromValues(auxValues, settings);
            if (!hasQty && qtyFromHelper > 0) {
                hasQty = { qty: qtyFromHelper };
            }
            const paxQtySelectorQuestions = settings?.questions?.filter(q => q.type === 'pax-qty-selector') || [];

            if (hasKeyTotal && hasKeyTotal.total > 0) {
                handleOnChange(auxValues);
            } else if (hasKeyDates && hasKeyDates.dates && hasKeyDates.dates.length > 0) {
                handleOnChange(auxValues);
                setResultColoquial('');
            } else if (hasQty && hasQty.qty && parseInt(hasQty.qty) > 0) {
                handleOnChange(auxValues);
            } else if (paxQtySelectorQuestions.length > 0 && auxValues.some(item => {
                const keys = Object.keys(item);
                return keys.some(k => paxQtySelectorQuestions.some(q => q.key === k || q.label === k));
            })) {
                handleOnChange(auxValues);
            } else {
                handleOnChange([]);
            }
            // Una sola fuente de verdad: misma lógica que página dedicada (getSettingsBookButtonDisabled)
            setDisableBookButton(getSettingsBookButtonDisabled(auxValues, settings));
        }
    }, [values]);

    const handleOnChangeOption = (el, fieldName = null, fieldValue = null) => {
        // Permitir que se pasen el nombre y valor directamente (para onPriceChange y onChange)
        const name = fieldName || el?.target?.name;
        const value = fieldValue !== null ? fieldValue : el?.target?.value;
        let auxValues = [...values];
        const keyExists = auxValues.findIndex(item => item[name] !== undefined);

        // Para arrays (como dates), verificar que no esté vacío
        const isEmptyArray = Array.isArray(value) && value.length === 0;

        if ((value || value === 0) && !isEmptyArray) {
            // Si la key existe, actualizar; si no, agregar
            if (keyExists >= 0) {
                auxValues[keyExists] = { [name]: value };
            } else {
                auxValues.push({ [name]: value });
            }
            
            // ✅ Ordenar fechas de forma ascendente (si es el campo 'dates')
            if (name === 'dates') {
                let hasKeyDates = auxValues.find(item => item['dates'] !== undefined);
                if (hasKeyDates && Array.isArray(hasKeyDates.dates)) {
                    hasKeyDates.dates = hasKeyDates.dates.sort((a, b) => new Date(a) - new Date(b));
                }
            }
            
            setValues(auxValues);
        } else {
            // Si el valor es vacio o null, elimino el valor
            // Para campos de mes (month-*), mantenerlos si hay fechas seleccionadas
            if (!name.startsWith('month-')) {
                auxValues = auxValues.filter(el1 => el1[name] === undefined);
                // También eliminar el total cuando se deselecciona una opción
                auxValues = auxValues.filter(el1 => el1['total'] === undefined);
                // Eliminar todos los meses asociados al campo de fechas
                auxValues = auxValues.filter(el1 => {
                    const keys = Object.keys(el1);
                    return !keys.some(k => k.startsWith('month-'));
                });
            }
            
            setValues(auxValues);
        }
        let hasKeyTotal = auxValues.find(item => item['total'] !== undefined);
        
        // ✅ Mostrar resultado coloquial según el tipo de addon
        if (name === 'total') {
            if (value === null) {
                setResultColoquial('');
            } else if (hasKeyTotal && hasKeyTotal.total > 0) {
                setResultColoquial(`${T('general.total')}: ${currencyFormat(hasKeyTotal.total, currency)}`);
            }
        } else if ((name === 'qty' || settings?.questions?.some(q => q.key === name && (q.type === 'select' || q.type === 'pax-qty-selector'))) && auxValues.length > 0 && !hasKeyTotal && !isNaN(value)) {
            // ✅ FIX: Usar el precio base original (basePrice) si está disponible, sino usar addon.price
            const basePrice = addon?.basePrice !== undefined ? addon.basePrice : (addon?.price || 0);
            const totalPrice = parseInt(value, 10) * basePrice;
            setResultColoquial(`${T('general.total')}: ${currencyFormat(totalPrice, currency)}`);
        } else if (addon?.price && auxValues.length > 0 && !hasKeyTotal && !isNaN(value) && name !== 'qty') {
            // Si es otro campo (no qty) con addon.price
            setResultColoquial(`${T('general.total')}: ${currencyFormat(parseInt(value) * addon.price, currency)}`);
        } else if (addon?.BookableItem?.allDay == true && Array.isArray(value)) {
            // Si es allDay (array de fechas)
            setResultColoquial(`${T('general.total')}: ${currencyFormat((parseInt(value.length) * addon.price), currency)}`);
        } else {
            // En otros casos, no mostrar nada
            setResultColoquial('');
        }

        if (name !== 'qty' && name !== 'total' && addon?.BookableItem?.allDay != true) {
            let hasKeyDates = auxValues.find(item => item['dates'] !== undefined);
            if ((!hasKeyTotal || (hasKeyTotal && (hasKeyTotal?.total === undefined || hasKeyTotal?.total <= 0))) 
                && (!hasKeyDates || (hasKeyDates && hasKeyDates.dates && hasKeyDates.dates.length === 0))) {
                setResultColoquial('');
            }
        }
    }

    const handleOnChangeText = (el) => {
        let auxValues = [...values];
        const name = el.target.name;
        const value = el.target.value;
        const keyExists = auxValues.findIndex(item => item[name] !== undefined);
        if (value) {
            // Si la key existe, actualizar; si no, agregar
            if (keyExists >= 0) {
                auxValues[keyExists] = { [name]: value };
            } else {
                auxValues.push({ [name]: value });
            }
            setValues(auxValues);
        } else {
            // Si el valor es vacío, elimino el valor
            auxValues = auxValues.filter(el1 => el1[name] === undefined);
            setValues(auxValues);
        }
    }

    const handleOnKeyDown = (e) => {
        // Permite letras, números, guiones, espacios y Ñ para patentes de vehículos
        const regex = /^[a-zA-Z0-9\s\-ñÑ]$/;
        const allowedKeys = ['Backspace', 'Space', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
      
        if (!regex.test(e.key) && !allowedKeys.includes(e.key)) {
          e.preventDefault();
        }
      };

    return (
        <>
            {type && (
                <div>

                    {/* <div className="">{type}</div>
                    <div className="">{JSON.stringify(data)}</div> */}

                    {type == "questions" && (
                        <div className='upsellings-questions'>
                            {/* <div className='pb-1'>Questions:</div> */}
                            {data.map((el, idx) => {
                                // Obtener el valor actual para este campo
                                const currentValue = values.find(v => v[el.key])?.[el.key] || (el.type === 'date-selector' ? [] : null);

                                if (el.type === 'totals') {
                                    return (
                                        <QuestionTotals
                                            key={`upsellings-questions-${idx}`}
                                            values={values}
                                            settings={settings}
                                            currency={currency}
                                            addon={addon}
                                            language={language}
                                            onPriceChange={handlePriceChange}
                                            roomCategory={checkin?.RoomCategory?.code}
                                            useRoomCategory={el?.useRoomCategory === true}
                                            checkin={checkin}
                                        />
                                    );
                                }

                                if (el.type === 'pax-qty-selector') {
                                    return (
                                        <QuestionPaxQtySelector
                                            key={`upsellings-questions-${idx}`}
                                            values={values}
                                            settings={settings}
                                            paxQuestion={el}
                                            currency={currency}
                                            addon={addon}
                                            language={language}
                                            checkin={checkin}
                                            onChange={(updatedValues) => {
                                                setValues(updatedValues);
                                                if (handleOnChange) {
                                                    handleOnChange(updatedValues);
                                                }
                                            }}
                                        />
                                    );
                                }
                                if (el.type === 'date-selector') {
                                    // Obtener fechas del checkin (dateStart y dateEnd)
                                    const startDate = checkin?.dateStart || el.startDate;
                                    const endDate = checkin?.dateEnd || el.endDate;

                                    console.log('Settings - date-selector:', { checkin, startDate, endDate, el });
                                    return (
                                        <QuestionSelectDay
                                            hideToday={el?.hideToday || false}
                                            hideCheckoutDate={el?.hideCheckoutDate || false}
                                            key={`upsellings-questions-${idx}`}
                                            startDate={startDate}
                                            endDate={endDate}
                                            multiple={el.multiple}
                                            value={currentValue}
                                            useCheckinDate={el?.useCheckinDate === true}
                                            useCheckoutDate={el?.useCheckoutDate === true}
                                            onChange={(selectedDates, selectedMonths) => {
                                                // ✅ CAMBIO: Hacer una única actualización en lugar de múltiples
                                                let auxValues = [...values];
                                                
                                                // 1. Actualizar o agregar las fechas seleccionadas
                                                const dateKeyExists = auxValues.findIndex(item => item[el.key] !== undefined);
                                                
                                                if (selectedDates && selectedDates.length > 0) {
                                                    // Si hay fechas, agregarlas o actualizarlas
                                                    if (dateKeyExists >= 0) {
                                                        auxValues[dateKeyExists] = { [el.key]: selectedDates };
                                                    } else {
                                                        auxValues.push({ [el.key]: selectedDates });
                                                    }
                                                    
                                                    // 2. Si addMonthtsToValues es true, agregar los meses EN LA MISMA ACTUALIZACIÓN
                                                    if (el.addMonthtsToValues == true) {
                                                        // ✅ Primero eliminar TODOS los meses anteriores
                                                        auxValues = auxValues.filter(item => {
                                                            const keys = Object.keys(item);
                                                            return !keys.some(k => k.startsWith('month-'));
                                                        });
                                                        
                                                        // Luego agregar solo los meses seleccionados actualmente
                                                        Object.keys(selectedMonths).forEach(month => {
                                                            const monthKey = `month-${month}`;
                                                            auxValues.push({ [monthKey]: selectedMonths[month] });
                                                        });
                                                    }
                                                } else {
                                                    // Si no hay fechas, limpiar todo
                                                    auxValues = auxValues.filter(item => {
                                                        const keys = Object.keys(item);
                                                        return !keys.some(k => k === el.key || k.startsWith('month-'));
                                                    });
                                                }
                                                
                                                // ✅ Ordenar fechas de forma ascendente (merge de otra rama)
                                                let hasKeyDates = auxValues.find(item => item['dates'] !== undefined);
                                                if (hasKeyDates) {
                                                    hasKeyDates.dates = hasKeyDates.dates?.sort((a, b) => new Date(a) - new Date(b));
                                                }
                                                
                                                // Actualizar estado UNA SOLA VEZ
                                                setValues(auxValues);
                                                // ✅ NO habilitar el botón aquí
                                                // Dejar que QuestionTotals valide y llame a onPriceChange
                                                // Esto es crítico para evitar el botón habilitado cuando hay precios inválidos
                                                // 
                                                // Si deseleccionas fechas, QuestionTotals llamará onPriceChange(null)
                                                // Si seleccionas fechas, QuestionTotals llamará onPriceChange(total)
                                                
                                                console.log('28ene upsellings - onChange final values:', auxValues);
                                            }}
                                            bookableItemId={addon?.BookableItem?.id}
                                            initialSelectedAll={el?.initialSelectedAll || false}
                                        />
                                    );
                                }

                                return (
                                    <div key={`upsellings-questions-${idx}`} className='upsellings-questions-item'>
                                        <Row className='d-flex justify-content-between align-items-center mt-2'>
                                            <Col xs={7}>
                                                {!el.labelI18n && <span>{el.label}</span>}
                                                {el.labelI18n && <span>{i18nValue(el, 'label', language)}</span>}
                                            </Col>
                                            <Col xs={5}>
                                                {
                                                    el.values
                                                        ?
                                                        <Form.Control
                                                            as={'select'}
                                                            name={el.key}
                                                            className='text-center'
                                                            onChange={(e) => handleOnChangeOption(e)}
                                                        >
                                                            <option value={""}>{T("select...")}</option>
                                                            {el.values?.map((el1, idx1) => (
                                                                <option key={`upsellings-questions-${idx}-option-${idx1}`} value={el1}>{el1}</option>
                                                            ))}

                                                        </Form.Control>
                                                        :
                                                        <Form.Control
                                                            name={el.key}
                                                            id={el.key}
                                                            className='text-center'
                                                            onChange={(e) => handleOnChangeText(e)}
                                                            onBlur={(e) => handleOnChangeText(e)}
                                                            onKeyDown={(e) => handleOnKeyDown(e)}
                                                        >
                                                        </Form.Control>
                                                }
                                            </Col>
                                        </Row>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <Row className="text-center my-2">
                        <Col xs={12}>{resultColoquial}</Col>
                    </Row>

                    {showButtons && (
                        <>
                            <Row className="d-flex align-items-end flex-grow-1 mb-4">
                                {showCancel && <Col xs={6} style={{ paddingRight: '5px' }}>
                                    <Button
                                        className='w-100'
                                        size={'lg'}
                                        variant={'secondary'}
                                        type={'button'}
                                        onClick={() => {
                                            onCancel()
                                            // setShowDateSelector(false)
                                        }}
                                    >
                                        {T('general.cancel')}
                                    </Button>
                                </Col>}
                                <Col xs={showCancel ? 6 : 12} style={{ paddingLeft: '5px' }}>
                                    <Button
                                        className='w-100'
                                        size={'lg'}
                                        variant={'success'}
                                        onClick={() => handleAddToCart(values)}
                                        disabled={disableBookButton}
                                    >
                                        {T('general.reserve')}
                                    </Button>
                                </Col>
                            </Row>
                        </>
                    )}
                </div>
            )}
        </>

    );
};

export default Settings;
